import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import parse from "html-react-parser";
import DOMPurify from "dompurify";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import { toast } from "react-toastify";
import "../styles/global.css";

/* ── Reusable star-rating component ── */
const StarRow = ({ score = 4.5, count }) => {
  const full = Math.floor(score);
  const half = score % 1 >= 0.5;
  return (
    <div className="clinic-doctor-rating-row">
      <span className="clinic-doctor-stars">
        {[...Array(5)].map((_, i) => (
          <i
            key={i}
            className={
              i < full
                ? "fa-solid fa-star"
                : i === full && half
                  ? "fa-solid fa-star-half-stroke"
                  : "fa-regular fa-star"
            }
          />
        ))}
      </span>
      <span className="clinic-doctor-rating-text">
        {score}/5{count ? ` (${count})` : ""}
      </span>
    </div>
  );
};

/* ── Tab config ── */
const TABS = [
  { key: "Overview",     icon: "fa-solid fa-circle-info",      label: "Overview" },
  { key: "Causes",       icon: "fa-solid fa-triangle-exclamation", label: "Causes" },
  { key: "Symptoms",     icon: "fa-solid fa-stethoscope",      label: "Symptoms" },
  { key: "CommonTypes",  icon: "fa-solid fa-layer-group",      label: "Common Types" },
  { key: "Doctors",      icon: "fa-solid fa-user-doctor",      label: "Doctors" },
];

/* ── Render plain text as bullet list (split by newline or comma) ── */
const BulletList = ({ text, emptyMsg = "Information not available." }) => {
  if (!text || !text.trim()) {
    return <p className="trd-empty-msg">{emptyMsg}</p>;
  }

  const raw = text.trim();

  // Try splitting by various delimiters in priority order
  let lines = [];

  if (raw.includes("\n")) {
    // Split by newline — handles textarea input with line breaks
    lines = raw.split("\n").map((l) => l.replace(/^[-•*]\s*/, "").trim()).filter(Boolean);
  } else if (raw.includes(".") && raw.split(".").filter(s => s.trim().length > 3).length > 1) {
    // Split by period — handles "Sentence one. Sentence two. Sentence three."
    lines = raw.split(".").map((l) => l.trim()).filter((l) => l.length > 2).map((l) => l + ".");
  } else if (raw.includes(",")) {
    // Split by comma — handles "item one, item two, item three"
    lines = raw.split(",").map((l) => l.trim()).filter(Boolean);
  } else if (raw.includes(";")) {
    // Split by semicolon
    lines = raw.split(";").map((l) => l.trim()).filter(Boolean);
  }

  // If we got meaningful items, render as bullet list
  if (lines.length > 1) {
    return (
      <ul className="trd-bullet-list">
        {lines.map((line, i) => (
          <li key={i} className="trd-bullet-item">
            <span className="trd-bullet-dot">
              <i className="fa-solid fa-circle-dot" />
            </span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
    );
  }

  // Single block of text — render as styled paragraph
  return <p className="trd-plain-text">{raw}</p>;
};

/* ── Info Card wrapper ── */
const InfoCard = ({ icon, title, children, accentColor = "#1E7A8C" }) => (
  <div className="trd-info-card">
    <div className="trd-info-card-header" style={{ "--accent": accentColor }}>
      <div className="trd-info-card-icon-wrap" style={{ background: accentColor + "18", color: accentColor }}>
        <i className={icon} />
      </div>
      <h3 className="trd-info-card-title">{title}</h3>
    </div>
    <div className="trd-info-card-body">{children}</div>
  </div>
);

const FrontTreatmentDetails = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [clinic, setClinic] = useState(null);
  const [imagesSwip, setImages] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [activeDate, setActiveDate] = useState("");
  const [activeTab, setActiveTab] = useState("Overview");

  /* ── Fancybox init ── */
  useEffect(() => {
    Fancybox.bind('[data-fancybox="gallery"]', {
      Thumbs: { autoStart: true },
      Toolbar: {
        display: {
          left: ["infobar"],
          middle: ["zoomIn", "zoomOut", "toggle1to1", "rotateCCW", "rotateCW", "flipX", "flipY"],
          right: ["slideshow", "thumbs", "close"],
        },
      },
    });
    return () => { Fancybox.destroy(); };
  }, []);

  /* ── API fetch ── */
  const fetchClinic = async (slug) => {
    const response = await fetch(`${API_BASE_URL}api/treatment/${slug}`);
    const json = await response.json();
    if (json.status) {
      setClinic(json.data);
      const images = json.data.image ? json.data.image.split(",") : [];
      setImages(images);
    }
  };

  useEffect(() => {
    fetchClinic(slug);
    const today = new Date().toISOString().split("T")[0];
    setActiveDate(today);
  }, [slug]);

  /* ── Device ID ── */
  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("device_id", deviceId);
  }

  /* ── Derived values ── */
  const heroImage = clinic?.image?.length
    ? `${API_BASE_URL}public/treatment/${clinic.image.split(",")[0]}`
    : `${API_BASE_URL}public/noimage.jpg`;

  /* ── Filter tabs — hide if data missing ── */
  const visibleTabs = TABS.filter((t) => {
    if (t.key === "Causes")      return !!clinic?.causes;
    if (t.key === "Symptoms")    return !!clinic?.symptoms;
    if (t.key === "CommonTypes") return !!clinic?.common_types;
    return true;
  });

  return (
    <div className="clinic-page">
      <Header />

      {/* ── Breadcrumb ── */}
      <div className="premium-breadcrumb cl-breadcrumb-bar">
        <div className="container-fluid">
          <ol className="clinic-breadcrumb-list d-flex">
            <li className="breadcrumb-item">
              <Link to="/" className="text-grey">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/treatments" className="text-grey">Treatment</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {clinic?.name || "Treatment Detail"}
            </li>
          </ol>
        </div>
      </div>

      {/* ── Hero ── */}
      <div className="clinic-hero">
        <div className="container clinic-hero-inner">
          <div className="clinic-hero-card">
            <img
              src={heroImage}
              alt={clinic?.name || "Treatment"}
              className="clinic-hero-img"
            />
            <div className="clinic-hero-body">
              <h1 className="clinic-hero-name">{clinic?.name || "—"}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs Bar ── */}
      <div className="clinic-tabs-bar">
        <div className="container">
          <div className="clinic-tabs-inner">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                className={`clinic-tab-btn${activeTab === t.key ? " active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                <i className={`${t.icon} clinic-tab-icon`} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <section className="clinic-main-section">
        <div className="container">
          <div className="clinic-layout">
            <div>

              {/* ════ OVERVIEW TAB ════ */}
              {activeTab === "Overview" && (
                <>
                  <InfoCard
                    icon="fa-solid fa-circle-info"
                    title="About the Treatment"
                    accentColor="#1E7A8C"
                  >
                    <p className="trd-plain-text">
                      {clinic?.desc || "No description available."}
                    </p>
                  </InfoCard>

                  {/* Quick-look cards for causes / symptoms / types if they exist */}
                  {(clinic?.causes || clinic?.symptoms || clinic?.common_types) && (
                    <div className="trd-overview-grid">
                      {clinic?.causes && (
                        <div
                          className="trd-overview-mini-card"
                          onClick={() => setActiveTab("Causes")}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && setActiveTab("Causes")}
                        >
                          <div className="trd-omc-icon" style={{ background: "#fff3e0", color: "#e65100" }}>
                            <i className="fa-solid fa-triangle-exclamation" />
                          </div>
                          <div>
                            <p className="trd-omc-label">Causes</p>
                            <p className="trd-omc-sub">
                              {clinic.causes.split(/[\n,]/)[0].trim().substring(0, 50)}
                              {clinic.causes.length > 50 ? "…" : ""}
                            </p>
                          </div>
                          <i className="fa-solid fa-chevron-right trd-omc-arrow" />
                        </div>
                      )}
                      {clinic?.symptoms && (
                        <div
                          className="trd-overview-mini-card"
                          onClick={() => setActiveTab("Symptoms")}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && setActiveTab("Symptoms")}
                        >
                          <div className="trd-omc-icon" style={{ background: "#e8f5e9", color: "#2e7d32" }}>
                            <i className="fa-solid fa-stethoscope" />
                          </div>
                          <div>
                            <p className="trd-omc-label">Symptoms</p>
                            <p className="trd-omc-sub">
                              {clinic.symptoms.split(/[\n,]/)[0].trim().substring(0, 50)}
                              {clinic.symptoms.length > 50 ? "…" : ""}
                            </p>
                          </div>
                          <i className="fa-solid fa-chevron-right trd-omc-arrow" />
                        </div>
                      )}
                      {clinic?.common_types && (
                        <div
                          className="trd-overview-mini-card"
                          onClick={() => setActiveTab("CommonTypes")}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && setActiveTab("CommonTypes")}
                        >
                          <div className="trd-omc-icon" style={{ background: "#e3f2fd", color: "#1565c0" }}>
                            <i className="fa-solid fa-layer-group" />
                          </div>
                          <div>
                            <p className="trd-omc-label">Common Types</p>
                            <p className="trd-omc-sub">
                              {clinic.common_types.split(/[\n,]/)[0].trim().substring(0, 50)}
                              {clinic.common_types.length > 50 ? "…" : ""}
                            </p>
                          </div>
                          <i className="fa-solid fa-chevron-right trd-omc-arrow" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Doctors preview on Overview */}
                  <div className="clinic-card mt-3">
                    <div className="clinic-doctors-header">
                      <h3 className="clinic-card-title clinic-card-title--no-border">Our Doctors</h3>
                      <Link to="/doctor-list-page" className="clinic-view-all-link">View All →</Link>
                    </div>
                    <DoctorGrid doctors={clinic?.doctors} />
                  </div>
                </>
              )}

              {/* ════ CAUSES TAB ════ */}
              {activeTab === "Causes" && (
                <InfoCard
                  icon="fa-solid fa-triangle-exclamation"
                  title="Causes"
                  accentColor="#e65100"
                >
                  {clinic?.causes ? (
                    <p className="trd-desc-text">{clinic.causes}</p>
                  ) : (
                    <p className="trd-empty-msg">Causes information not available.</p>
                  )}
                </InfoCard>
              )}

              {/* ════ SYMPTOMS TAB ════ */}
              {activeTab === "Symptoms" && (
                <InfoCard
                  icon="fa-solid fa-stethoscope"
                  title="Symptoms"
                  accentColor="#2e7d32"
                >
                  {clinic?.symptoms ? (
                    <p className="trd-desc-text">{clinic.symptoms}</p>
                  ) : (
                    <p className="trd-empty-msg">Symptoms information not available.</p>
                  )}
                </InfoCard>
              )}

              {/* ════ COMMON TYPES TAB ════ */}
              {activeTab === "CommonTypes" && (
                <InfoCard
                  icon="fa-solid fa-layer-group"
                  title="Common Types"
                  accentColor="#1565c0"
                >
                  {clinic?.common_types ? (
                    <p className="trd-desc-text">{clinic.common_types}</p>
                  ) : (
                    <p className="trd-empty-msg">Common types information not available.</p>
                  )}
                </InfoCard>
              )}

              {/* ════ DOCTORS TAB ════ */}
              {activeTab === "Doctors" && (
                <div className="clinic-card">
                  <div className="clinic-doctors-header">
                    <h3 className="clinic-card-title clinic-card-title--no-border">All Doctors</h3>
                    <Link to="/doctor-list-page" className="clinic-view-all-link">View All →</Link>
                  </div>
                  <DoctorGrid doctors={clinic?.doctors} />
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      {/* ── Inline styles ── */}
      <style>{`
        /* ── Tab icon ── */
        .clinic-tab-icon {
          margin-right: 6px;
          font-size: 13px;
          opacity: 0.8;
        }
        .clinic-tab-btn.active .clinic-tab-icon { opacity: 1; }

        /* ── Info Card ── */
        .trd-info-card {
          background: #fff;
          border: 1px solid #e8f0ee;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 2px 14px rgba(0,0,0,.05);
          margin-bottom: 20px;
        }
        .trd-info-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 22px;
          border-bottom: 1px solid #f0f5f3;
          background: linear-gradient(135deg, #f8fbfa 0%, #f2f8f5 100%);
        }
        .trd-info-card-icon-wrap {
          width: 40px; height: 40px;
          border-radius: 11px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; flex-shrink: 0;
        }
        .trd-info-card-title {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
          color: #0f1f1a;
          letter-spacing: -0.2px;
        }
        .trd-info-card-body {
          padding: 22px;
        }

        /* ── Bullet list ── */
        .trd-bullet-list {
          list-style: none;
          margin: 0; padding: 0;
          display: flex; flex-direction: column; gap: 12px;
        }
        .trd-bullet-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 16px;
          background: #f8fbfa;
          border-radius: 10px;
          border: 1px solid #edf2f0;
          font-size: 14.5px;
          color: #2d3d38;
          line-height: 1.55;
          transition: background .15s, border-color .15s;
        }
        .trd-bullet-item:hover {
          background: #eef7f3;
          border-color: #c8e6d8;
        }
        .trd-bullet-dot {
          color: #1E7A8C;
          font-size: 12px;
          margin-top: 3px;
          flex-shrink: 0;
        }
        .trd-plain-text {
          font-size: 14.5px;
          color: #3d4f4a;
          line-height: 1.8;
          margin: 0;
          padding: 14px 18px;
          background: #f8fbfa;
          border-radius: 10px;
          border: 1px solid #edf2f0;
          white-space: pre-line;
        }
        .trd-desc-text {
          font-size: 15px;
          color: #2d3d38;
          line-height: 1.85;
          margin: 0;
          white-space: pre-line;
        }
        .trd-empty-msg {
          font-size: 14px;
          color: #9aaba5;
          font-style: italic;
          margin: 0;
        }

        /* ── Overview mini-cards ── */
        .trd-overview-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }
        .trd-overview-mini-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          background: #fff;
          border: 1px solid #e8f0ee;
          border-radius: 14px;
          cursor: pointer;
          transition: all .18s;
          box-shadow: 0 1px 6px rgba(0,0,0,.04);
        }
        .trd-overview-mini-card:hover {
          border-color: #1E7A8C;
          background: #f4fbf8;
          transform: translateX(3px);
          box-shadow: 0 4px 16px rgba(30,122,140,.12);
        }
        .trd-omc-icon {
          width: 44px; height: 44px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
        }
        .trd-omc-label {
          font-size: 14px; font-weight: 700;
          color: #0f1f1a; margin: 0 0 2px;
        }
        .trd-omc-sub {
          font-size: 12.5px; color: #7a8f88; margin: 0;
          line-height: 1.4;
        }
        .trd-omc-arrow {
          margin-left: auto;
          color: #b0c4be;
          font-size: 13px;
          flex-shrink: 0;
        }
        .trd-overview-mini-card:hover .trd-omc-arrow {
          color: #1E7A8C;
        }

        @media (max-width: 576px) {
          .trd-info-card-header { padding: 14px 16px; }
          .trd-info-card-body   { padding: 16px; }
          .trd-bullet-item { font-size: 13.5px; padding: 10px 12px; }
        }
      `}</style>

      <Footer />
    </div>
  );
};

/* ── Doctor grid — extracted to avoid repetition ── */
const DoctorGrid = ({ doctors }) => {
  if (!doctors?.length) {
    return <p className="clinic-empty-msg">No doctors listed.</p>;
  }
  return (
    <div className="clinic-doctor-grid">
      {doctors.map((doctor) => (
        <div key={doctor.id} className="clinic-doctor-card">
          <div className="clinic-doctor-card-top">
            <div className="clinic-doctor-img-wrap">
              <Link to={`/doctordetails/${doctor.slug}`}>
                <img
                  src={
                    doctor.images
                      ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}`
                      : `${API_BASE_URL}public/noimage.jpg`
                  }
                  alt={doctor.name}
                  className="clinic-doctor-img"
                />
              </Link>
              <div className="clinic-doctor-verify-badge">
                <i className="fa-solid fa-check" />
              </div>
            </div>
            <div className="clinic-doctor-meta">
              <Link to={`/doctordetails/${doctor.slug}`} className="clinic-doctor-name">
                {doctor?.name}
              </Link>
              <p className="clinic-doctor-spec">{doctor?.department_title?.name}</p>
              <StarRow score={4.5} />
              <div className="clinic-doctor-views">
                <i className="fa-solid fa-chart-line" />
                4,138 views
              </div>
            </div>
          </div>

          <div className="clinic-doctor-card-body">
            {doctor?.specializations && (
              <div className="clinic-doctor-pills">
                {doctor.specializations.split(",").slice(0, 3).map((s, i) => (
                  <span key={i} className="clinic-pill">{s.trim()}</span>
                ))}
              </div>
            )}
            <p className="clinic-doctor-desc">{doctor?.short_desc}</p>
            <div className="clinic-doctor-tags">
              {doctor?.experience && (
                <span>
                  <i className="fa-solid fa-briefcase" />
                  {doctor.experience}
                </span>
              )}
              <span className="clinic-degree-tag">B.A.MS & M.S.</span>
            </div>
          </div>

          <div className="clinic-doctor-card-footer">
            <Link to={`/doctordetails/${doctor.slug}`} className="dc-book-btn">
              Book Consultation
            </Link>
            <div className="clinic-fee-tag">
              {doctor?.videocall_price == 0 ? (
                <span className="clinic-fee-amount clinic-fee-amount--free">Free</span>
              ) : (
                <>
                  <span className="clinic-fee-amount">₹ {doctor?.videocall_price}</span>
                  Consult fee
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FrontTreatmentDetails;